"use strict";
/**
 * Manager and main owner of the game round sequence.
 *
 *
 * Created: 2019-08-09
 * @author jonas
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameRoundManager = void 0;
const gsap_1 = require("gsap");
const GameModeTransitionManager_1 = require("../gamemode/GameModeTransitionManager");
const ZeroBetManager_1 = require("../../../core/zerobet/ZeroBetManager");
const EventHandler_1 = require("../../../core/event/EventHandler");
const ScreenEvent_1 = require("../../../core/screen/event/ScreenEvent");
const GameEvent_1 = require("../../../core/event/GameEvent");
const SpinEvent_1 = require("../../../core/spin/event/SpinEvent");
const GameModeEvent_1 = require("../../../core/gamemode/event/GameModeEvent");
const ServerEvent_1 = require("../../../core/server/event/ServerEvent");
const JurisdictionEvent_1 = require("../../../core/jurisdiction/JurisdictionEvent");
const GameMode_1 = require("../../../core/gamemode/GameMode");
const Logger_1 = require("@nolimitcity/slot-launcher/bin/utils/Logger");
const SlotGame_1 = require("../../../core/SlotGame");
const APIEventSystem_1 = require("@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem");
const SlotStateHandler_1 = require("@nolimitcity/slot-launcher/bin/plugins/apiplugin/SlotStateHandler");
const ReelEvent_1 = require("../../../core/reel/event/ReelEvent");
/**
 * Internal state for game round.
 */
var GameRoundState;
(function (GameRoundState) {
    GameRoundState[GameRoundState["NONE"] = 0] = "NONE";
    GameRoundState[GameRoundState["IDLE"] = 1] = "IDLE";
    GameRoundState[GameRoundState["STARTING"] = 2] = "STARTING";
    GameRoundState[GameRoundState["STOPPABLE"] = 3] = "STOPPABLE";
    GameRoundState[GameRoundState["ABORT_STOPPABLE"] = 4] = "ABORT_STOPPABLE";
    GameRoundState[GameRoundState["SKIPPABLE"] = 5] = "SKIPPABLE";
    GameRoundState[GameRoundState["ABORT_SKIPPABLE"] = 6] = "ABORT_SKIPPABLE";
    GameRoundState[GameRoundState["IDLE_LOOP"] = 7] = "IDLE_LOOP";
})(GameRoundState || (GameRoundState = {}));
/**
 Internal states for Presentation.
 */
var PresentationState;
(function (PresentationState) {
    PresentationState[PresentationState["INACTIVE"] = 0] = "INACTIVE";
    PresentationState[PresentationState["ACTIVE"] = 1] = "ACTIVE";
    PresentationState[PresentationState["DONE"] = 2] = "DONE";
})(PresentationState || (PresentationState = {}));
/**
 * Manager to control game flow and abort events from api.
 */
class GameRoundManager {
    constructor(gameRound) {
        this._state = GameRoundState.NONE;
        this._skippableIndex = 0;
        this._jurisdictionPadding = 0;
        this._allReelsStarted = false;
        this._stopConditions = {
            timeline: false,
            reels: false
        };
        this._isHalt = false;
        this._transitionManager = new GameModeTransitionManager_1.GameModeTransitionManager();
        this._zeroBetManager = new ZeroBetManager_1.ZeroBetManager();
        this._sequences = gameRound;
        this._minimumGameRoundTimeInMs = 0;
        this.addEventListeners();
        Logger_1.Logger.logDev("%c GameFlow: GameRoundManager ", `background-color:#777777;; color:white; font-size: 12px; padding: 4px;`);
    }
    addEventListeners() {
        EventHandler_1.EventHandler.addLastEventListener(this, ScreenEvent_1.ScreenEvent.GAME_START, (event) => this.onGameStart());
        SlotGame_1.SlotGame.api.events.on(APIEventSystem_1.APIEvent.BET, (data) => this.onBet(data));
        SlotGame_1.SlotGame.api.events.on(APIEventSystem_1.APIEvent.STATE, (state) => this.onState(state));
        SlotGame_1.SlotGame.api.events.on(APIEventSystem_1.APIEvent.HALT, () => this.onHalt());
        EventHandler_1.EventHandler.addEventListener(this, SpinEvent_1.SpinEvent.STOP, (event) => this.onAbort());
        EventHandler_1.EventHandler.addEventListener(this, SpinEvent_1.SpinEvent.SKIP, (event) => this.onAbort());
        EventHandler_1.EventHandler.addEventListener(this, ServerEvent_1.ServerEvent.INIT_DATA_PARSED, (event) => this.onInitData(event.params[0]));
        EventHandler_1.EventHandler.addEventListener(this, ServerEvent_1.ServerEvent.GAME_DATA_PARSED, (event) => this.onGameData(event.params[0]));
        EventHandler_1.EventHandler.addEventListener(this, GameModeEvent_1.GameModeEvent.GAME_MODE_TRANSITION_FINISHED, (event) => this.onGameModeTransitionFinished(event));
        EventHandler_1.EventHandler.addEventListener(this, JurisdictionEvent_1.JurisdictionEvent.MINIMUM_STOP_TIME, (event) => this.onMinimumStopTime(event));
        EventHandler_1.EventHandler.addLastEventListener(this, ReelEvent_1.ReelEvent.ALL_REELS_SPIN_STOPPED, (event) => this.onAllReelsSpinStopped());
        EventHandler_1.EventHandler.addEventListener(this, ReelEvent_1.ReelEvent.ALL_REELS_SPIN_STARTED, (event) => this.onAllReelsSpinStarted());
    }
    killMainTimeline() {
        if (this._tl) {
            this._tl.kill();
            delete this._tl;
        }
    }
    onHalt() {
        this._isHalt = true;
        this.killMainTimeline();
    }
    onState(state) {
        switch (state) {
            case SlotStateHandler_1.SlotState.GAMBLE_DONE:
                this.checkTransition(false);
                break;
        }
    }
    //Event handlers
    onInitData(data) {
        EventHandler_1.EventHandler.removeEventListener(this, ServerEvent_1.ServerEvent.INIT_DATA_PARSED);
        this._mode = data.mode;
        this._oldData = data;
        this._data = data;
    }
    onGameData(data) {
        this._mode = data.mode;
        this._data = data;
        if (this._state == GameRoundState.STARTING) {
            this.playStoppable();
        }
    }
    onMinimumStopTime(event) {
        this._minimumGameRoundTimeInMs = event.params[0];
    }
    onGameStart() {
        EventHandler_1.EventHandler.removeLastEventListener(this, ScreenEvent_1.ScreenEvent.GAME_START);
        this.checkTransition(true);
    }
    onGameModeTransitionFinished(event) {
        this._mode = event.toMode;
        if (this._mode == GameMode_1.GameMode.NORMAL) {
            this.finishGameRound();
            this.startGameRound();
        }
        else {
            this.startGameRound();
        }
    }
    onBet(data) {
        this._betData = data;
        this.bet();
    }
    startGameRound() {
        this._oldData = this._data == null ? this._oldData : this._data;
        this._data = null;
        this.playIdle();
    }
    bet() {
        if (this._state == GameRoundState.IDLE || this._state == GameRoundState.IDLE_LOOP) {
            this.abort();
        }
        else if (this._state == GameRoundState.NONE && this._betData) {
            if (this._mode != "GAMBLE") {
                this.playStarting();
            }
            this._betData = undefined;
        }
        else {
            throw new Error("Bet was made during non compatible GameRound state");
        }
    }
    onAbort() {
        var _a;
        if (this._state == GameRoundState.STOPPABLE) {
            if ((_a = this._tl) === null || _a === void 0 ? void 0 : _a.isActive()) {
                this.abort();
            }
        }
        else if (this._state == GameRoundState.SKIPPABLE) {
            this.abort();
        }
        else {
            Logger_1.Logger.warn("You can only abort during STOPPABLE or SKIPPABLE");
            return;
        }
    }
    abort() {
        this.killMainTimeline();
        const bufferTime = 100;
        const tl = this.createAbortTimeline(bufferTime, this._data);
        let abortState;
        let continueFunction;
        switch (this._state) {
            case GameRoundState.IDLE:
            case GameRoundState.IDLE_LOOP:
                abortState = GameRoundState.NONE;
                continueFunction = () => this.bet();
                break;
            case GameRoundState.STOPPABLE:
                abortState = GameRoundState.ABORT_STOPPABLE;
                continueFunction = () => {
                    this._stopConditions.timeline = true;
                    this.tryCompleteStoppable();
                };
                break;
            case GameRoundState.SKIPPABLE:
                abortState = GameRoundState.ABORT_SKIPPABLE;
                if (this._skippableIndex < this._sequences[this._mode].skippable.length) {
                    continueFunction = () => {
                        this.playSkippable();
                    };
                }
                else {
                    this._skippableIndex = 0;
                    continueFunction = () => {
                        EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.DONE));
                        this.checkTransition(false);
                    };
                }
                break;
            default:
                throw new Error("Aborted non abort state");
        }
        this._tl = tl;
        this._tl.add(() => {
            this.setState(abortState);
        }, bufferTime);
        this._tl.eventCallback("onComplete", () => continueFunction());
        this._tl.seek(bufferTime - 0.01, true);
    }
    setState(state) {
        GameRoundManager.logDebug("GameRound[" + this._mode + "] - " + GameRoundState[this._state] + " end");
        this._state = state;
        GameRoundManager.logDebug("GameRound[" + this._mode + "] - " + GameRoundState[this._state] + " start");
    }
    playIdle() {
        if (this._isHalt) {
            return;
        }
        this._controlStates = [];
        this.setState(GameRoundState.IDLE);
        this._tl = new gsap_1.TimelineLite();
        const idleAnimations = this.createStateTimeline(this._oldData, this._sequences[this._mode].idle);
        if (idleAnimations != null) {
            this._tl.add(idleAnimations);
        }
        this._tl.eventCallback("onComplete", () => this.playIdleLoop());
    }
    playIdleLoop() {
        if (this._isHalt) {
            return;
        }
        this._controlStates = [];
        this._tl = new gsap_1.TimelineLite();
        const loopAnimation = this.createStateTimeline(this._oldData, this._sequences[this._mode].idleLoop);
        if (loopAnimation != null) {
            this._tl.add(() => {
                this.setState(GameRoundState.IDLE_LOOP);
            });
            this._tl.add(loopAnimation);
            this._tl.eventCallback("onComplete", () => this.playIdleLoop());
        }
        else {
            this._tl.add(() => {
                this.setState(GameRoundState.NONE);
            });
        }
    }
    playStarting() {
        if (this._isHalt) {
            return;
        }
        this._controlStates = [];
        this.killMainTimeline();
        this._tl = new gsap_1.TimelineLite({ onComplete: () => this.playStoppable() });
        this._tl.add(() => {
            this._spinStartTime = Date.now();
            this.setState(GameRoundState.STARTING);
        });
        this._tl.add(this.createStateTimeline(this._oldData, this._sequences[this._mode].starting));
    }
    onAllReelsSpinStarted() {
        this._allReelsStarted = true;
        this.playStoppable();
    }
    playStoppable() {
        var _a;
        if (this._isHalt) {
            return;
        }
        //Checks if both data and previous state timeline is complete before entering next state.
        if (this._data != null && !((_a = this._tl) === null || _a === void 0 ? void 0 : _a.isActive()) && this._allReelsStarted) {
            this._allReelsStarted = false;
            this._controlStates = [];
            this._tl = new gsap_1.TimelineLite({
                onStart: () => {
                    EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.STARTED));
                    this.setState(GameRoundState.STOPPABLE);
                },
                onComplete: () => {
                    this._stopConditions.timeline = true;
                    this.tryCompleteStoppable();
                }
            });
            const stoppingAnimations = this.createStateTimeline(this._data, this._sequences[this._mode].stoppable);
            if (stoppingAnimations != null) {
                this._jurisdictionPadding = this.calculateMinimumSpinPadding(stoppingAnimations, this._mode);
                this._tl.add(stoppingAnimations, this._jurisdictionPadding);
            }
        }
    }
    onAllReelsSpinStopped() {
        this._stopConditions.reels = true;
        this.tryCompleteStoppable();
    }
    tryCompleteStoppable() {
        if (this._stopConditions.timeline && this._stopConditions.reels) {
            EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.STOPPED));
            this.spinComplete();
            this.playSkippable();
            this._stopConditions.timeline = false;
            this._stopConditions.reels = false;
        }
    }
    playSkippable() {
        if (this._isHalt) {
            return;
        }
        this._controlStates = [];
        this._tl = new gsap_1.TimelineLite();
        const skippableAnimations = this.createStateTimeline(this._data, this._sequences[this._mode].skippable[this._skippableIndex]);
        if (skippableAnimations != null) {
            this._tl.add(() => {
                EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.WANT_SKIPPABLE));
                this.setState(GameRoundState.SKIPPABLE);
            });
            this._tl.add(skippableAnimations);
        }
        this._skippableIndex++;
        if (this._skippableIndex < this._sequences[this._mode].skippable.length) {
            this._tl.eventCallback("onComplete", () => {
                this.playSkippable();
            });
        }
        else {
            this._tl.add(() => {
                this._skippableIndex = 0;
            });
            this._tl.eventCallback("onComplete", () => {
                EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.DONE));
                this.checkTransition(false);
            });
        }
    }
    checkTransition(isInit) {
        if (this._transitionManager.tryToMakeTransition()) {
            return;
        }
        if (!isInit && this._mode == GameMode_1.GameMode.NORMAL) {
            this.finishGameRound();
        }
        this.startGameRound();
    }
    finishGameRound() {
        EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.FINISHING));
        // Tells the api the round is finished.
        EventHandler_1.EventHandler.dispatchEvent(new GameEvent_1.GameEvent(SpinEvent_1.SpinEvent.FINISH));
    }
    //Timeline creation
    createStateTimeline(data, presentations) {
        let tl = null;
        let startTime = 0;
        for (let presentation of presentations) {
            if (presentation.prepare) {
                presentation.prepare(data);
            }
            const presentationAnimation = presentation.getAnimation(data);
            if (presentationAnimation == null) {
                continue;
            }
            if (tl == null) {
                tl = new gsap_1.TimelineLite();
            }
            const controlTl = this.addControlState(presentationAnimation, presentation);
            tl.add(controlTl, startTime);
            //Timing control.
            let delay = controlTl.duration();
            if (presentation.stagger != undefined && presentation.stagger >= 0) {
                delay = presentation.stagger;
            }
            startTime += delay;
        }
        return tl;
    }
    createAbortTimeline(bufferTime, data) {
        const tl = new gsap_1.TimelineLite();
        let startTime = bufferTime;
        tl.add(new gsap_1.TweenLite(this, bufferTime, {}));
        for (let control of this._controlStates) {
            if (control && control.tl != undefined) {
                control.tl.kill();
            }
            if (!control.presentation.abort || control.state == PresentationState.DONE) {
                //Has no abort function, or is done already.
                continue;
            }
            let presentationAnimation;
            if (control.state == PresentationState.ACTIVE) {
                presentationAnimation = control.presentation.abort(true, data);
            }
            else {
                presentationAnimation = control.presentation.abort(false, data);
            }
            if (presentationAnimation == null) {
                continue;
            }
            tl.add(presentationAnimation, startTime - presentationAnimation.time());
            //Timing control.
            if (control.presentation.abortStagger != undefined && control.presentation.abortStagger >= 0) {
                startTime += control.presentation.abortStagger;
            }
            else {
                startTime = tl.totalDuration();
            }
        }
        return tl;
    }
    //Utils
    addControlState(animation, presentation) {
        const tl = new gsap_1.TimelineLite();
        const control = {
            presentation: presentation,
            state: PresentationState.INACTIVE,
            tl: tl
        };
        this._controlStates.push(control);
        tl.add(animation);
        tl.eventCallback("onStart", () => {
            control.state = PresentationState.ACTIVE;
        });
        tl.eventCallback("onComplete", () => {
            control.state = PresentationState.DONE;
        });
        return tl;
    }
    calculateMinimumSpinPadding(stoppingAnimations, mode) {
        if (mode != GameMode_1.GameMode.NORMAL) {
            return 0;
        }
        const timeFromStartInMs = Date.now() - this._spinStartTime;
        const padding = (this._minimumGameRoundTimeInMs - timeFromStartInMs - (stoppingAnimations.totalDuration() * 1000)) / 1000;
        const finalPadding = Math.max(0, padding);
        return finalPadding;
    }
    //UTILS:
    static logDebug(message) {
        if (GameRoundManager.debugLog) {
            Logger_1.Logger.logDev(message);
        }
    }
    /**
     * Removes a timeline from the parent timeline and plays it on it's own. Useful if a presentation is aborted but the animation should continue anyway and not stop the GameRound from continuing.
     * For example a Win count up End presentation that should continue to fade out even when a new GameRound is started.
     *
     * @param timelineToDetach the timeline to play detached from the GameRound timeline.
     */
    static detachAndPlayTimeline(timelineToDetach) {
        timelineToDetach.timeline.remove(timelineToDetach);
        timelineToDetach.play();
    }
    spinComplete(quickStop = false) {
        const quickStopString = quickStop ? " quick stopped" : "";
        const jurPaddingString = this._jurisdictionPadding > 0 ? " Jur padding: " + this._jurisdictionPadding : "";
        Logger_1.Logger.logDev(`%cGameRoundManager measured total spin duration: ${(Date.now() - this._spinStartTime)}${jurPaddingString}${quickStopString}`, `background-color:#ccccff; `);
    }
}
GameRoundManager.debugLog = true;
exports.GameRoundManager = GameRoundManager;
//# sourceMappingURL=GameRoundManager.js.map